import type { PatchOperation } from '@medplum/core';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { Communication } from '@medplum/fhirtypes';
import {
  buildNotificationSearchParams,
  getCurrentNotificationRecipientReference,
  isNotificationCommunication,
  isUnreadNotification,
} from '../lib/notificationFhir';
import { usePortalSubject } from '../contexts/PortalSubjectContext';
import { useFhirRealtime } from './useFhirRealtime';

export interface UseInAppNotificationsReturn {
  notifications: Communication[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  /** Mark a single notification as read. */
  markAsRead: (commId: string) => Promise<void>;
  /** Mark all currently unread notifications as read. */
  markAllAsRead: () => Promise<void>;
  /** Manually re-fetch notifications. */
  refetch: () => Promise<void>;
}

export function useInAppNotifications(): UseInAppNotificationsReturn {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const location = useLocation();
  const { activePatientId } = usePortalSubject();

  const [notifications, setNotifications] = useState<Communication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const recipientRef = useMemo(
    () => getCurrentNotificationRecipientReference(profile, activePatientId),
    [profile, activePatientId],
  );

  const load = useCallback(async () => {
    if (!recipientRef) {
      setNotifications([]);
      setLoading(false);
      return;
    }
    try {
      setError(null);
      // Invalidate the client-side cache so reconnect and tab-focus refreshes
      // always read fresh data from the server, not a stale cached search.
      medplum.invalidateSearches('Communication');
      const params = buildNotificationSearchParams(recipientRef, { count: 50 });
      const results = await medplum.searchResources('Communication', params);
      setNotifications(results.filter(isNotificationCommunication));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load notifications');
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [medplum, recipientRef]);

  useEffect(() => {
    void load();
  }, [load, location.pathname]);

  const { onNotificationRefresh } = useFhirRealtime();

  useEffect(() => {
    return onNotificationRefresh(() => {
      void load();
    });
  }, [onNotificationRefresh, load]);

  // Refresh when the tab becomes visible (catches events missed while backgrounded).
  useEffect(() => {
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  const unreadCount = useMemo(
    () => notifications.filter(isUnreadNotification).length,
    [notifications],
  );

  const markAsRead = useCallback(
    async (commId: string) => {
      const comm = notifications.find((n) => n.id === commId);
      if (!comm || comm.status === 'completed') return;

      const ops: PatchOperation[] = [
        { op: 'replace', path: '/status', value: 'completed' },
      ];
      const now = new Date().toISOString();
      if (comm.received) {
        ops.push({ op: 'replace', path: '/received', value: now });
      } else {
        ops.push({ op: 'add', path: '/received', value: now });
      }

      setNotifications((prev) =>
        prev.map((n) =>
          n.id === commId ? { ...n, status: 'completed', received: now } : n,
        ),
      );

      try {
        await medplum.patchResource('Communication', commId, ops);
      } catch (e) {
        console.warn('[Notifications] markAsRead failed, refetching', e);
        void load();
      }
    },
    [medplum, notifications, load],
  );

  const markAllAsRead = useCallback(async () => {
    const unread = notifications.filter(isUnreadNotification);
    if (unread.length === 0) return;

    const now = new Date().toISOString();
    setNotifications((prev) =>
      prev.map((n) =>
        n.status === 'in-progress' ? { ...n, status: 'completed', received: now } : n,
      ),
    );

    const results = await Promise.allSettled(
      unread.map((n) => {
        if (!n.id) return Promise.resolve();
        const ops: PatchOperation[] = [
          { op: 'replace', path: '/status', value: 'completed' },
        ];
        if (n.received) {
          ops.push({ op: 'replace', path: '/received', value: now });
        } else {
          ops.push({ op: 'add', path: '/received', value: now });
        }
        return medplum.patchResource('Communication', n.id, ops);
      }),
    );

    const hasFailed = results.some((r) => r.status === 'rejected');
    if (hasFailed) {
      console.warn('[Notifications] Some markAllAsRead patches failed, refetching');
      void load();
    }
  }, [medplum, notifications, load]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    refetch: load,
  };
}
